import type { BoardState, BoardStore } from '../store/store'
import { projectForPath } from '../projects/routing'

/** A tab owns its project choice; every entity/read/mutation still belongs to the one BoardStore. */
export function projectStore(shared: BoardStore, initial: string | null = null, path: () => string = () => '') {
  let selected = initial
  let source: BoardState | null = null
  let cached: BoardState
  let address = ''
  const listeners = new Set<() => void>()
  const getState = () => {
    const next = shared.getState()
    if (source !== next || address !== path()) {
      source = next
      address = path()
      selected = projectForPath(address, [...next.projects.values()])?.id
        ?? (selected !== null && next.projects.has(selected) ? selected : next.selectedProjectId)
      cached = { ...next, selectedProjectId: selected }
    }
    return cached
  }
  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    const off = shared.subscribe(listener)
    return () => { listeners.delete(listener); off() }
  }
  const selectProject = (id: string) => {
    if (!shared.getState().projects.has(id) || id === selected) return
    selected = id
    source = null
    for (const listener of listeners) listener()
  }
  return new Proxy(shared, {
    get(target, key) {
      if (key === 'getState') return getState
      if (key === 'subscribe') return subscribe
      if (key === 'selectProject') return selectProject
      const value = Reflect.get(target, key, target)
      return typeof value === 'function' ? value.bind(target) : value
    },
  })
}
